import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog"
import { Trash2 } from 'lucide-react';
import { Input } from "@/components/ui/input"
import { Pencil } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { fetchWithAuth } from '@/lib/apiUtils';

// Prompt types - Use established database convention
const PROMPT_TYPES = [
  { value: 'highlight_system', label: 'Highlight System' }, 
  { value: 'highlight_user', label: 'Highlight User' },   
  { value: 'poem_system', label: 'Poem System' }, // Corrected
  { value: 'poem_user', label: 'Poem User' }     // Corrected
];

// Define the PromptTemplate type to match our API
interface PromptTemplate {
  id: string;
  name: string;
  template_text: string;
  prompt_type?: string; // Making this optional as we're transitioning away from it
  version: number;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  is_current?: boolean;
  friendly_name?: string;
}

const AdminPrompts: React.FC = () => {
  const { toast } = useToast();
  const { session, loading } = useAuth();
  const [selectedType, setSelectedType] = useState<string>(PROMPT_TYPES[0].value);
  const [isLoadingList, setIsLoadingList] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string>('current');
  const [selectedTemplate, setSelectedTemplate] = useState<PromptTemplate | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [currentContent, setCurrentContent] = useState<string>('');
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [isEditingFriendlyName, setIsEditingFriendlyName] = useState(false);
  const [editedFriendlyName, setEditedFriendlyName] = useState<string>('');
  const [isSavingFriendlyName, setIsSavingFriendlyName] = useState(false);
  // State for the "Save New Version" dialog
  const [isSaveDialogVisible, setIsSaveDialogVisible] = useState(false);
  const [newVersionFriendlyName, setNewVersionFriendlyName] = useState('');

  const fetchTemplateList = useCallback(async () => {
    if (!session || loading) {
       console.log(`Auth not ready (loading: ${loading}, session: ${!!session}), skipping fetchTemplateList`);
       setTemplates([]);
       setSelectedTemplate(null);
       setCurrentContent('');
       setIsLoadingList(false);
       return;
    }

    console.log(`Fetching template LIST for type: ${selectedType} (Authenticated)`);
    setIsLoadingList(true);
    setTemplates([]);
    setSelectedTemplate(null);
    setCurrentContent('');

    try {
      const response = await fetchWithAuth(`/api/prompt-templates/type/${selectedType}`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `HTTP error ${response.status}: ${response.statusText}` }));
        throw new Error(errorData.message || `Failed to fetch template list: ${response.statusText}`);
      }
      const data: PromptTemplate[] = await response.json() || [];
      const sortedTemplates = data.sort((a, b) => b.version - a.version);
      setTemplates(sortedTemplates);
      console.log('Fetched template list:', sortedTemplates);

      let finalSelectedVersionId = selectedVersionId;
      if (selectedVersionId === 'current') {
          const activeTemplate = sortedTemplates.find(t => t.is_active);
          if (!activeTemplate && sortedTemplates.length > 0) {
              console.log("No active version found, keeping dropdown on 'current' (will load latest).");
          } else if (sortedTemplates.length === 0) {
              console.log("No templates exist, keeping dropdown on 'current' (disabled).");
          }
      } else {
           const stillExists = sortedTemplates.some(t => t.id === selectedVersionId);
           if (!stillExists) {
                console.warn(`Previously selected version ${selectedVersionId} not found in new list, reverting dropdown to current.`);
                finalSelectedVersionId = 'current';
           }
      }

      if (finalSelectedVersionId !== selectedVersionId) {
          setSelectedVersionId(finalSelectedVersionId);
      }

    } catch (error) {
      console.error('Error fetching template list:', error);
      toast({ title: 'Error', description: error instanceof Error ? error.message : 'Failed to fetch template list', variant: 'destructive' });
    } finally {
      setIsLoadingList(false);
      console.log('fetchTemplateList FINISHED. isLoadingList:', false);
    }
  }, [selectedType, toast, session, loading, selectedVersionId]);

  const fetchTemplateContent = useCallback(async (id: string | null) => {
    if (!session || loading) {
        console.log(`Auth not ready (loading: ${loading}, session: ${!!session}), skipping fetchTemplateContent`);
        setCurrentContent('');
        setSelectedTemplate(null);
        setIsLoadingContent(false);
        return;
    }

    if (!id) {
      console.log("No ID provided for content fetch, clearing content.");
      setCurrentContent('');
      setSelectedTemplate(null);
      setIsLoadingContent(false);
      return;
    }

    console.log(`Fetching content for template ID: ${id} (Authenticated)`);
    setIsLoadingContent(true);
    setCurrentContent('');

    try {
      const response = await fetchWithAuth(`/api/prompt-templates/${id}`);
      if (!response.ok) {
        if (response.status === 401) {
            console.error("Unauthorized fetching template content - this shouldn't happen if auth check passed.");
            toast({ title: 'Authorization Error', description: 'Your session might have expired. Please refresh.', variant: 'destructive' });
        } else if (response.status === 404) {
            console.warn(`Template content not found (ID: ${id}). It might have been deleted.`);
             toast({ title: 'Not Found', description: 'Selected template version not found. It might have been deleted.', variant: 'default' });
             fetchTemplateList();
        } else {
            const errorData = await response.json().catch(() => ({ message: `HTTP error ${response.status}: ${response.statusText}` }));
            throw new Error(errorData.message || `Failed to fetch template content: ${response.statusText}`);
        }
        setSelectedTemplate(null);
        setCurrentContent('Error: Template not found or could not be loaded.');
      } else {
        const data: PromptTemplate = await response.json();
        console.log('Fetched content data:', data);
        setCurrentContent(data.template_text || '');
        setSelectedTemplate(data);
      }
    } catch (error) {
      console.error('Error fetching template content:', error);
      setCurrentContent('Error loading template content.');
      setSelectedTemplate(null);
      toast({ title: 'Error', description: error instanceof Error ? error.message : 'Could not load template content', variant: 'destructive' });
    } finally {
      setIsLoadingContent(false);
      console.log(`fetchTemplateContent FINISHED for ID: ${id}. isLoadingContent:`, false);
    }
  }, [toast, fetchTemplateList, session, loading]);

  useEffect(() => {
    console.log(`useEffect[selectedType, session, loading] RUNNING. Fetching list for: ${selectedType}`);
    if (session && !loading) {
        fetchTemplateList();
    } else if (loading) {
        console.log("Auth is loading, deferring initial fetchTemplateList call.");
    } else {
        console.log("User not authenticated, not calling fetchTemplateList.");
        setTemplates([]);
        setSelectedTemplate(null);
        setCurrentContent('');
    }
  }, [selectedType, fetchTemplateList, session, loading]);

  useEffect(() => {
    console.log("useEffect[templates, selectedVersionId, ...] RUNNING. State:", {
        isLoadingList,
        templatesLength: templates.length,
        selectedVersionId,
        selectedTemplateId: selectedTemplate?.id,
    });

    let idToFetch: string | null = null;

    if (templates.length === 0 && !isLoadingList) {
        console.log("Template list is empty, not fetching content.");
        setCurrentContent('');
        setSelectedTemplate(null);
        return;
    }

    if (selectedVersionId === 'current') {
      const activeTemplate = templates.find(t => t.is_active);
      idToFetch = activeTemplate ? activeTemplate.id : (templates[0]?.id || null);
      if (!activeTemplate && templates.length > 0) {
         console.log("Dropdown is on 'current', no active version found, resolving to fetch latest version's content by ID:", idToFetch);
      } else if (activeTemplate) {
         console.log("Dropdown is on 'current', resolving to fetch active version's content by ID:", idToFetch);
      }
    } else {
      idToFetch = selectedVersionId;
      console.log("Dropdown is on specific ID, resolving to fetch content for ID:", idToFetch);
    }

    if (idToFetch) {
        if (selectedTemplate?.id !== idToFetch) {
            fetchTemplateContent(idToFetch);
        } else {
             console.log("Content for target ID already loaded, skipping fetch.");
        }
    } else {
        console.log("useEffect[templates, selectedVersionId, ...] COULD NOT RESOLVE idToFetch. Clearing content.");
        setCurrentContent(''); 
        setSelectedTemplate(null);
    }

  }, [selectedVersionId, templates, fetchTemplateContent, isLoadingList, selectedTemplate?.id]);

  // Function to trigger the save dialog
  const handleOpenSaveDialog = () => {
    // Pre-populate with current name as suggestion, or empty if none
    setNewVersionFriendlyName(selectedTemplate?.friendly_name || ''); 
    setIsSaveDialogVisible(true);
  };

  // Actual save function (to be called by the dialog later)
  const handleConfirmSave = async (friendlyName: string) => {
    const contentToSave = currentContent;
    const nameToSave = friendlyName.trim();

    if (!contentToSave.trim()) {
      toast({ title: 'Validation Error', description: 'Prompt content cannot be empty', variant: 'destructive' });
      return false; // Indicate failure
    }
    if (!nameToSave) {
      toast({ title: 'Validation Error', description: 'Friendly name cannot be empty', variant: 'destructive' });
      return false; // Indicate failure
    }

    console.log(`Saving new version for ${selectedType} with friendly name: "${nameToSave}" (Authenticated)`);
    setIsSaving(true);
    let success = false;
    try {
      const response = await fetchWithAuth('/api/prompt-templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt_type: selectedType,
          name: selectedType,
          template_text: contentToSave,
          friendly_name: nameToSave
        }),
      });
      if (!response.ok) {
          let errorDetails = `Failed to save template: ${response.statusText}`;
          try {
              const errorData = await response.json();
              errorDetails = errorData.message || errorDetails;
          } catch (jsonError) { /* Ignore */ }
          throw new Error(errorDetails); 
      }
      const newTemplate = await response.json();
      toast({ title: 'Success', description: `Version ${newTemplate.version} (${nameToSave}) saved successfully.` });
      setSelectedVersionId('current'); 
      fetchTemplateList();
      success = true;
    } catch (error) {
      console.error('Error saving template:', error);
      toast({ title: 'Error', description: error instanceof Error ? error.message : 'Failed to save template', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
    return success;
  };

  const handleTypeChange = (type: string) => {
    if (type !== selectedType) {
      console.log(`Tab changed to ${type}`);
      setSelectedType(type);
      setSelectedVersionId('current');
    }
  };

  const handleVersionChange = (versionId: string) => {
    console.log("Dropdown version selected:", versionId);
    setSelectedVersionId(versionId);
  };

  const setAsCurrent = async (id: string | undefined) => {
    if (!id) return;
    console.log(`Setting template ${id} as current (Authenticated)`);
    try {
      const response = await fetchWithAuth(`/api/prompt-templates/${id}/set-current`, { method: 'POST' });
      if (!response.ok) { 
          const errorData = await response.json().catch(() => ({ message: `Request failed: ${response.statusText}` })); 
          throw new Error(errorData.message || `Failed to set as current: ${response.statusText}`); 
      }
      toast({ title: 'Success', description: 'Template set as current' });
      setSelectedVersionId(id);
      fetchTemplateList();
    } catch (error) {
      console.error('Error setting as current:', error);
      toast({ title: 'Error', description: 'Failed to set as current', variant: 'destructive' });
    }
  };

  const deleteTemplate = async (id: string | undefined) => {
    if (!id) return;
    console.log(`Attempting to delete template ${id} (Authenticated)`);
    setIsDeleting(true);
    try {
      const response = await fetchWithAuth(`/api/prompt-templates/${id}`, { method: 'DELETE' });
      if (!response.ok) {
        if (response.status >= 400 && response.status < 500) {
          let errorData = { message: `Request failed with status ${response.status}` };
          try { errorData = await response.json(); } catch (jsonError) { console.warn('Could not parse error response as JSON:', jsonError); }
          console.warn(`Deletion prevented/failed: ${errorData.message}`);
          toast({ title: 'Deletion Prevented/Failed', description: errorData.message || 'Could not delete this template version.', variant: 'default' });
        } else {
          throw new Error(`Failed to delete template: ${response.statusText}`);
        }
      } else {
        toast({ title: 'Success', description: 'Template version deleted successfully' });
        setSelectedVersionId('current');
        fetchTemplateList();
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast({ title: 'Error', description: 'Failed to delete template', variant: 'destructive' });
    } finally {
      setIsDeleting(false);
    }
  };
  
  const isViewingCurrentActive = selectedTemplate?.is_active ?? false;
  const isDropdownOnSpecificVersion = selectedVersionId !== 'current';

  // Handle starting the edit of friendly name
  const handleEditFriendlyNameStart = () => {
      if (selectedTemplate) {
          setEditedFriendlyName(selectedTemplate.friendly_name || '');
          setIsEditingFriendlyName(true);
      }
  };

  // Handle cancelling the edit
  const handleEditFriendlyNameCancel = () => {
      setIsEditingFriendlyName(false);
      setEditedFriendlyName(''); // Clear the temporary state
  };

  // Handle saving the edited friendly name
  const handleEditFriendlyNameSave = async () => {
      if (!selectedTemplate) return;
      
      const finalFriendlyName = editedFriendlyName.trim() || null; // Send null if empty
      if (finalFriendlyName === (selectedTemplate.friendly_name || null)) {
          setIsEditingFriendlyName(false);
          return; // No change needed
      }

      console.log(`Saving friendly name for ${selectedTemplate.id}: ${finalFriendlyName} (Authenticated)`);
      setIsSavingFriendlyName(true);

      try {
          const response = await fetchWithAuth(`/api/prompt-templates/${selectedTemplate.id}`, { 
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ friendly_name: finalFriendlyName }), 
          });

          if (!response.ok) {
              const errorData = await response.json().catch(() => ({ message: `Request failed: ${response.statusText}` }));
              throw new Error(errorData.message || `Failed to update friendly name: ${response.statusText}`);
          }
          
          const updatedData = await response.json();
          toast({ title: 'Success', description: 'Friendly name updated.' });
          
          setIsEditingFriendlyName(false);
          fetchTemplateList(); 

      } catch (error) {
          console.error('Error updating friendly name:', error);
          toast({ title: 'Error', description: error instanceof Error ? error.message : 'Failed to update friendly name', variant: 'destructive' });
      } finally {
          setIsSavingFriendlyName(false);
      }
  };

  return (
    <div className="container mx-auto py-8">
      <h1 className="text-3xl font-bold mb-6">Prompt Management</h1>
      
      <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-md p-4 mb-6">
        <h3 className="text-lg font-semibold mb-2">Developer Information</h3>
        <p className="mb-2">
          The highlight prompt system is transitioning to use only the <code className="bg-blue-100 px-1 rounded">name</code> field 
          for template identification instead of the <code className="bg-blue-100 px-1 rounded">prompt_type</code> field.
        </p>
        <ul className="list-disc list-inside ml-4 text-sm">
          <li>This UI will only interact with the <code className="bg-blue-100 px-1 rounded">highlight_generation_prompts</code> table</li>
          <li>Template identification is based on the <code className="bg-blue-100 px-1 rounded">name</code> field values ('highlight_system' or 'highlight_user')</li>
          <li>The <code className="bg-blue-100 px-1 rounded">prompt_type</code> field is now automatically derived from the name field</li>
          <li>API consumers should update their code to rely solely on the <code className="bg-blue-100 px-1 rounded">name</code> field for template identification</li>
        </ul>
      </div>
      
      <Tabs value={selectedType} onValueChange={handleTypeChange} className="w-full">
        <div className="flex justify-between items-center mb-6">
          <TabsList className="grid grid-cols-4 w-[600px]">
            {PROMPT_TYPES.map((type) => (
              <TabsTrigger 
                key={type.value} 
                value={type.value}
              >
                {type.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="flex flex-col items-end">
            <div>
              <Select 
                value={selectedVersionId}
                onValueChange={handleVersionChange}
                disabled={isLoadingList}
              >
                <SelectTrigger className="w-[300px]">
                  <SelectValue placeholder="Select Version" />
                </SelectTrigger>
                <SelectContent>
                   {isLoadingList ? (
                      <SelectItem value="loading" disabled>Loading...</SelectItem>
                   ) : templates.some(t => t.is_active) ? (
                      <SelectItem value="current">
                          Current Active Version (v{templates.find(t => t.is_active)?.version})
                      </SelectItem>
                   ) : templates.length > 0 ? (
                      <SelectItem value="current" disabled> 
                          No Active Version (Latest: v{templates[0].version})
                      </SelectItem>
                   ) : (
                      <SelectItem value="current" disabled>
                          No Versions Available
                      </SelectItem>
                   )}
                   {templates
                    .filter(t => !t.is_active)
                    .map((template) => (
                      <SelectItem 
                        key={template.id} 
                        value={template.id}
                      >
                        {template.friendly_name ? 
                          <span title={template.friendly_name}>{template.friendly_name} (v{template.version})</span> : 
                          <span>Version {template.version}</span>
                        }
                        <span className="text-xs text-muted-foreground ml-2"> - {new Date(template.updated_at).toLocaleDateString()}</span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2 mt-2">
              {isDropdownOnSpecificVersion && !isViewingCurrentActive && selectedTemplate && (
                <Button
                  variant="secondary"
                  onClick={() => setAsCurrent(selectedTemplate?.id)}
                  disabled={isLoadingList || isLoadingContent || isSaving || isDeleting}
                  size="sm"
                >
                  Set as Current
                </Button>
              )}
  
              <Button 
                onClick={handleOpenSaveDialog}
                disabled={isSaving || isLoadingList || isLoadingContent || isDeleting || !selectedTemplate || !currentContent.trim()} 
                title="Save the current editor content as a new version"
                size="sm"
              >
                {isSaving ? <LoadingSpinner className="mr-2 h-4 w-4" /> : null}
                Save as New Version
              </Button>
  
              {isDropdownOnSpecificVersion && !isViewingCurrentActive && selectedTemplate && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="destructive" 
                        size="icon"
                        disabled={isLoadingList || isLoadingContent || isSaving || isDeleting}
                        title={`Delete Version ${selectedTemplate.version}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This action cannot be undone. This will permanently delete 
                          Version {selectedTemplate.version} {selectedTemplate.friendly_name ? `(${selectedTemplate.friendly_name}) ` : ''} 
                          of the '{selectedTemplate.name}' template.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => deleteTemplate(selectedTemplate?.id)} 
                          disabled={isDeleting}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          {isDeleting ? <LoadingSpinner className="mr-2 h-4 w-4" /> : null}
                          Yes, Delete Version {selectedTemplate.version}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
              )}
            </div>
          </div>
        </div>

        {/* AlertDialog for Saving New Version */}
        <AlertDialog open={isSaveDialogVisible} onOpenChange={setIsSaveDialogVisible}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Save New Version</AlertDialogTitle>
              <AlertDialogDescription>
                Please provide a friendly name for this new version of the 
                '{PROMPT_TYPES.find(p => p.value === selectedType)?.label}' template.
                New versions are saved as inactive initially.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <label htmlFor="friendlyNameInput" className="block text-sm font-medium text-gray-700 mb-1">
                Friendly Name
              </label>
              <Input
                id="friendlyNameInput"
                placeholder="Enter friendly name (required)"
                value={newVersionFriendlyName}
                onChange={(e) => setNewVersionFriendlyName(e.target.value)}
                className="w-full"
                disabled={isSaving}
              />
              {/* Optional: Add validation feedback here */} 
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={async (e) => {
                  e.preventDefault(); // Prevent default dialog close
                  const success = await handleConfirmSave(newVersionFriendlyName);
                  if (success) {
                    setIsSaveDialogVisible(false); // Close dialog on success
                  }
                }} 
                disabled={isSaving || !newVersionFriendlyName.trim()}
              >
                {isSaving ? <LoadingSpinner className="mr-2 h-4 w-4" /> : null}
                Confirm Save
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Card>
          <CardHeader>
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0">
                <CardTitle>Edit Template</CardTitle>
                {selectedTemplate && !isEditingFriendlyName && (
                    <div className="flex items-center gap-2 mt-1">
                         <CardDescription>
                           {selectedTemplate.friendly_name ? 
                             <span className="font-semibold block truncate max-w-md" title={selectedTemplate.friendly_name}>{selectedTemplate.friendly_name}</span> : 
                             <span className="italic text-muted-foreground">No friendly name</span>
                           }
                         </CardDescription>
                         <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0" onClick={handleEditFriendlyNameStart} title="Edit friendly name">
                            <Pencil className="h-4 w-4" />
                         </Button>
                    </div>
                )}
                {selectedTemplate && isEditingFriendlyName && (
                    <div className="flex items-center gap-2 mt-1">
                        <Input 
                            value={editedFriendlyName}
                            onChange={(e) => setEditedFriendlyName(e.target.value)}
                            placeholder="Enter friendly name (optional)"
                            className="h-8"
                            disabled={isSavingFriendlyName}
                        />
                        <Button 
                            variant="secondary"
                            size="sm" 
                            onClick={handleEditFriendlyNameSave}
                            disabled={isSavingFriendlyName || editedFriendlyName === (selectedTemplate.friendly_name || '')}
                        >
                           {isSavingFriendlyName ? <LoadingSpinner className="h-4 w-4"/> : 'Save'}
                        </Button>
                        <Button 
                            variant="ghost"
                            size="sm"
                            onClick={handleEditFriendlyNameCancel}
                            disabled={isSavingFriendlyName}
                        >
                            Cancel
                        </Button>
                    </div>
                )}
                {!selectedTemplate && (
                     <CardDescription>
                       Editing template for: <span className="font-semibold">{PROMPT_TYPES.find(pt => pt.value === selectedType)?.label}</span>
                     </CardDescription>
                )}
              </div>
              {isLoadingList ? (
                  <div className="text-right text-sm text-muted-foreground flex-shrink-0">Loading list...</div>
              ) : selectedTemplate ? (
                <div className="text-right text-sm flex-shrink-0 max-w-[200px]">
                    {selectedTemplate.is_active ? (
                        <Badge variant="secondary" className="bg-green-100 text-green-800 border-green-300 whitespace-nowrap">
                           {selectedTemplate.friendly_name ? `${selectedTemplate.friendly_name} (v${selectedTemplate.version})` : `Version ${selectedTemplate.version}`} - Active
                        </Badge>
                    ) : (
                        <Badge variant="outline" className="whitespace-nowrap">
                           {selectedTemplate.friendly_name ? `${selectedTemplate.friendly_name} (v${selectedTemplate.version})` : `Version ${selectedTemplate.version}`} - Historical
                        </Badge>
                    )}
                    <p className="text-muted-foreground mt-1 truncate" title={new Date(selectedTemplate.updated_at).toLocaleString()}>
                        Last Updated: {new Date(selectedTemplate.updated_at).toLocaleString()}
                    </p>
                </div>
              ) : isLoadingContent ? (
                  <div className="text-right text-sm text-muted-foreground flex-shrink-0">Loading content...</div>
              ) : (
                  <div className="text-right text-sm text-muted-foreground flex-shrink-0">No version selected</div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isLoadingContent ? (
              <div className="flex justify-center items-center min-h-[200px]">
                <LoadingSpinner className="h-8 w-8" />
              </div>
            ) : (
              <Textarea
                placeholder={isLoadingList ? "Loading list..." : templates.length > 0 ? "Select a version to view or edit..." : "No templates available for this type."}
                value={currentContent}
                onChange={(e) => setCurrentContent(e.target.value)}
                className="min-h-[300px] flex-grow mt-4 font-mono"
                disabled={isLoadingContent || isLoadingList || !selectedTemplate}
              />
            )}
          </CardContent>
        </Card>
      </Tabs>
    </div>
  );
};

export default AdminPrompts; 